import { MdAssessment, MdChevronLeft, MdChevronRight } from "react-icons/md";

const VmCostTable = ({ data, loading, error, index, hasNext, hasPrevious, onPageChange }) => {
  const { vmCostHistory = [], months = [] } = data || {};

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatMonth = (monthStr) => {
    const [year, month] = monthStr.split('-');
    return `${month}-${year}`;
  };

  const handlePrevious = () => {
    if (hasPrevious && onPageChange) {
      onPageChange(index - 1);
    }
  };

  const handleNext = () => {
    if (hasNext && onPageChange) {
      onPageChange(index + 1);
    }
  };

  const reversedMonths = [...months].reverse().map(month => formatMonth(month));

  const reversedVmCostHistory = vmCostHistory.map(vm => ({
    ...vm,
    costs: [...vm.costs].reverse()
  }));

  if (loading) {
    return (
      <div className="bg-primary-dark rounded-2xl p-4 sm:p-6 m-2 sm:m-4 md:m-8">
        <div className="text-center py-8">
          <div className="inline-block w-8 h-8 border-4 border-primary-cyan border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-400 mt-3">Loading VM costs...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-primary-dark rounded-2xl p-4 sm:p-6 m-2 sm:m-4 md:m-8">
        <div className="text-center py-8">
          <p className="text-red-400">Error loading VM costs: {error}</p>
        </div>
      </div>
    );
  }

  if (!vmCostHistory || vmCostHistory.length === 0) {
    return (
      <div className="bg-primary-dark rounded-2xl p-4 sm:p-6 m-2 sm:m-4 md:m-8">
        <div className="flex items-center gap-2 mb-6 pb-3 border-b border-gray-700">
          <MdAssessment className="text-primary-cyan text-2xl" />
          <h3 className="text-xl font-semibold text-white">VM Cost History</h3>
        </div>
        <div className="text-center py-8">
          <p className="text-gray-400">No VM cost data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-primary-dark rounded-2xl p-4 sm:p-6 m-2 sm:m-4 md:m-8">
      <div className="flex items-center gap-2 mb-6 pb-3 border-b border-gray-700">
        <MdAssessment className="text-primary-cyan text-2xl" />
        <h3 className="text-xl font-semibold text-white">VM Cost History</h3>
      </div>

      <div className="overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0">
        <div className="min-w-[600px] sm:min-w-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-700">
                <th className="text-left py-3 px-2 sm:px-3 text-gray-400 font-medium text-xs sm:text-sm sticky left-0 bg-primary-dark z-10">
                  VM Name
                </th>
                {reversedMonths.map((month, idx) => (
                  <th key={idx} className="text-left py-3 px-2 sm:px-3 text-gray-400 font-medium text-xs sm:text-sm whitespace-nowrap">
                    {month}
                  </th>
                ))}
                </tr>
            </thead>
            <tbody>
              {reversedVmCostHistory.map((vm) => (
                <tr key={vm.vmId} className="border-b border-gray-700/50 hover:bg-white/5 transition">
                  <td className="py-3 px-2 sm:px-3 sticky left-0 bg-primary-dark z-10">
                    <span className="text-white font-medium text-sm break-words">
                      {vm.vmName}
                    </span>
                    </td>
                  {vm.costs.map((cost, idx) => (
                    <td key={idx} className="py-3 px-2 sm:px-3">
                      <span className="text-gray-300 text-xs sm:text-sm whitespace-nowrap">
                        {formatAmount(cost)}
                      </span>
                      </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {vmCostHistory.length > 0 && (
        <div className="flex justify-center items-center gap-2 sm:gap-4 mt-6 pt-4 border-t border-gray-700">
          <button
            onClick={handlePrevious}
            disabled={!hasPrevious}
            className={`
              flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 rounded-lg font-medium transition text-sm sm:text-base
              ${hasPrevious 
                ? "bg-primary-cyan hover:bg-primary-cyan/80 text-white" 
                : "bg-gray-700 text-gray-400 cursor-not-allowed"}
            `}
          >
            <MdChevronLeft className="w-3 h-3 sm:w-4 sm:h-4" />
            <span className="hidden sm:inline">Previous</span>
          </button>
          
          <span className="text-gray-400 text-xs sm:text-sm">
            Page {index + 1}
          </span>
          
          <button
            onClick={handleNext}
            disabled={!hasNext}
            className={`
              flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 rounded-lg font-medium transition text-sm sm:text-base
              ${hasNext 
                ? "bg-primary-cyan hover:bg-primary-cyan/80 text-white" 
                : "bg-gray-700 text-gray-400 cursor-not-allowed"}
            `}
          >
            <span className="hidden sm:inline">Next</span>
            <MdChevronRight className="w-3 h-3 sm:w-4 sm:h-4" />
          </button>
        </div>
      )}
    </div>
  );
};

export default VmCostTable;